import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getBusinessMember,
  updateBusinessMember,
  removeBusinessMember,
} from '@app/services/business';
import { BusinessMemberPermissions } from '@app/models/business';

export const useMemberProfile = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('profile');

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [editingInfoJobTitle, setEditingInfoJobTitle] = useState('');

  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<BusinessMemberPermissions>({});
  const [editingJobTitle, setEditingJobTitle] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'default';
    confirmText?: string;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const {
    data: member,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => getBusinessMember(memberId!),
    enabled: !!memberId,
  });

  const updateMemberMutation = useMutation({
    mutationFn: (data: {
      permissions?: BusinessMemberPermissions | null;
      jobTitle?: string | null;
    }) => updateBusinessMember(memberId!, data),
    onSuccess: () => {
      toast.success('Member updated successfully');
      queryClient.invalidateQueries({ queryKey: ['member', memberId] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update member');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: () => removeBusinessMember(memberId!),
    onSuccess: () => {
      toast.success('Member disconnected successfully');
      queryClient.invalidateQueries({ queryKey: ['members'] });
      navigate('/settings?tab=members');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to disconnect member');
    },
  });

  const handleOpenInfoModal = () => {
    if (!member) return;
    setEditingInfoJobTitle(member.jobTitle || '');
    setShowInfoModal(true);
  };

  const handleSaveInfo = () => {
    const newJobTitle = editingInfoJobTitle.trim();
    updateMemberMutation.mutate({ jobTitle: newJobTitle || null });
    setShowInfoModal(false);
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
    setEditingInfoJobTitle('');
  };

  const handleOpenPermissionsModal = () => {
    if (!member) return;
    setEditingPermissions((member.permissions as BusinessMemberPermissions) || {});
    setEditingJobTitle(member.jobTitle || '');
    setShowPermissionsModal(true);
  };

  const handleSavePermissions = () => {
    const hasAnyPermission = Object.values(editingPermissions).some((v) => v === true);
    const newJobTitle = editingJobTitle.trim();

    updateMemberMutation.mutate({
      permissions: hasAnyPermission ? editingPermissions : null,
      jobTitle: newJobTitle || null,
    });

    setShowPermissionsModal(false);
  };

  const handleClosePermissionsModal = () => {
    setShowPermissionsModal(false);
    setEditingPermissions({});
    setEditingJobTitle('');
  };

  const handleDisconnectMember = () => {
    if (!member) return;
    const name = member.user?.firstName && member.user?.lastName
      ? `${member.user.firstName} ${member.user.lastName}`
      : member.user?.email || 'this member';

    setConfirmDialog({
      open: true,
      title: 'Disconnect Member',
      message: `Are you sure you want to remove ${name} from this business? They will lose access to all business data.`,
      confirmText: 'Disconnect',
      variant: 'danger',
      onConfirm: () => {
        removeMemberMutation.mutate();
        setConfirmDialog((prev) => ({ ...prev, open: false }));
      },
    });
  };

  const handleGoBack = () => {
    navigate('/settings?tab=members');
  };

  return {
    member,
    isLoading,
    error,
    activeTab,
    setActiveTab,
    showInfoModal,
    editingInfoJobTitle,
    setEditingInfoJobTitle,
    handleOpenInfoModal,
    handleSaveInfo,
    handleCloseInfoModal,
    showPermissionsModal,
    editingPermissions,
    setEditingPermissions,
    editingJobTitle,
    setEditingJobTitle,
    confirmDialog,
    setConfirmDialog,
    updateMemberMutation,
    removeMemberMutation,
    handleOpenPermissionsModal,
    handleSavePermissions,
    handleClosePermissionsModal,
    handleDisconnectMember,
    handleGoBack,
  };
};
